import React, { useState, useEffect } from 'react';
import { TransportationInfo } from './types';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { PlacesAutocompleteV1 } from '@/components/ui/places-autocomplete-v1';
import { RichTextEditor } from '@/components/ui/rich-text-editor';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { 
  ImagePlus, 
  Wand2, 
  Car, 
  Trash, 
  ChevronDown, 
  ChevronRight,
  Calendar,
  Clock,
  MapPin,
  DollarSign,
  Loader2,
  Tag,
  FileText,
  Image,
  AlignLeft,
  CircleDot,
  ClipboardList,
  Users,
  Info,
  Briefcase,
  ArrowLeftRight,
  Bookmark,
  Copy,
  MoreHorizontal
} from 'lucide-react';
import { Card, CardHeader, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { ImageManagerModal } from '@/components/ui/image-manager-modal';
import { useOpenAiApi } from '@/hooks/useOpenAiApi';
import { useToast } from '@/hooks/use-toast';
import { SaveToLibraryModal } from './SaveToLibraryModal';
import { isItemSavedInLibrary } from '@/lib/library-utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

interface TransportationFormProps {
  transportation: TransportationInfo;
  index: number;
  pageId: string;
  updateTransportation: (pageId: string, transportationId: string, field: keyof TransportationInfo, value: any) => void;
  batchUpdateTransportation?: (transportationId: string, updates: Partial<TransportationInfo>) => void;
  deleteTransportation: (pageId: string, transportationId: string) => void;
  duplicateTransportation?: (pageId: string, transportationId: string) => void;
  moveTransportationToPage?: (transportationId: string) => void;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
}

// Custom styled label with orange underline and icon
const StyledLabel = ({ 
  children, 
  htmlFor, 
  className, 
  icon: Icon = CircleDot,
  ...props 
}: React.ComponentProps<typeof Label> & { icon?: React.ElementType }) => (
  <Label 
    htmlFor={htmlFor}
    className={cn("border-b border-voah-coral pb-1 inline-flex items-center gap-1.5 text-left text-sm font-medium text-gray-700", className)}
    {...props}
  >
    <Icon className="h-3.5 w-3.5 text-voah-coral flex-shrink-0" />
    <span className="whitespace-nowrap">{children}</span>
  </Label>
);

// Update input styles by creating a component wrapper
const FormInput = ({ className, ...props }: React.ComponentProps<typeof Input>) => (
  <Input
    className={cn("text-sm text-gray-600 font-normal", className)}
    {...props}
  />
);

const FormTextarea = ({ className, ...props }: React.ComponentProps<typeof Textarea>) => (
  <Textarea
    className={cn("text-sm text-gray-600 font-normal", className)}
    {...props}
  />
);

export function TransportationForm({
  transportation,
  index,
  pageId,
  updateTransportation,
  batchUpdateTransportation,
  deleteTransportation,
  duplicateTransportation,
  moveTransportationToPage,
  isCollapsed,
  onToggleCollapse
}: TransportationFormProps) {
  const { toast } = useToast();
  const { chatCompletion } = useOpenAiApi();
  const [isImageManagerOpen, setIsImageManagerOpen] = useState(false);
  const [isProcessingDescription, setIsProcessingDescription] = useState(false);
  const [isCalculatingTimes, setIsCalculatingTimes] = useState(false);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [isSaveToLibraryOpen, setIsSaveToLibraryOpen] = useState(false);
  const [isSavedToLibrary, setIsSavedToLibrary] = useState(false);
  const [transportationTitle, setTransportationTitle] = useState<string>(() => {
    return transportation.name || `Traslado #${index + 1}`;
  });

  // Effect to keep transportationTitle in sync with transportation.name
  useEffect(() => {
    const displayTitle = transportation.name?.trim() ? transportation.name : `Traslado #${index + 1}`;
    setTransportationTitle(displayTitle);
  }, [transportation.name, index]);

  // Debug effect to track transportation changes
  useEffect(() => {
    console.log("[TransportationForm] Transportation updated:", {
      id: transportation.id,
      location: transportation.location,
      coordinates: transportation.coordinates
    });
  }, [transportation.location, transportation.coordinates]);

  // Verificar se o item já está salvo na biblioteca
  useEffect(() => {
    let isMounted = true;
    
    const checkLibraryStatus = async () => {
      try {
        const isSaved = await isItemSavedInLibrary(transportation, 'transportation');
        if (isMounted) {
          setIsSavedToLibrary(isSaved);
        }
      } catch (error) {
        console.error('Erro ao verificar status na biblioteca:', error);
        if (isMounted) {
          setIsSavedToLibrary(false);
        }
      }
    };
    
    checkLibraryStatus();
    
    return () => {
      isMounted = false; // cleanup to prevent setting state on unmounted component
    };
  }, [transportation]);

  // Function to update a field
  const handleUpdate = (field: keyof TransportationInfo, value: any) => {
    let safeValue = value;
    
    // Special handling for coordinates to ensure they're properly formatted
    if (field === 'coordinates' && value) {
      const lat = typeof value.lat === 'string' ? parseFloat(value.lat) : Number(value.lat);
      const lng = typeof value.lng === 'string' ? parseFloat(value.lng) : Number(value.lng);
      
      safeValue = {
        lat: isNaN(lat) ? 0 : lat,
        lng: isNaN(lng) ? 0 : lng
      };
    }
    
    updateTransportation(pageId, transportation.id, field, safeValue);
  };

  // AI Description enhancement
  const handleProcessDescription = async () => {
    if (!transportation.description || transportation.description.trim() === '') {
      toast({
        title: "Descrição vazia",
        description: "Por favor, insira um texto na descrição para ser processado pela IA.",
        variant: "destructive"
      });
      return;
    }

    setIsProcessingDescription(true);
    
    try {
      // Extract text from HTML
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = transportation.description;
      const textContent = tempDiv.textContent || tempDiv.innerText || '';

      // Enviar apenas o texto do usuário, sem prompts adicionais
      const response = await chatCompletion(textContent);
      
      if (response && response.choices && response.choices.length > 0) {
        // Extrair o conteúdo da mensagem da resposta
        const generatedContent = response.choices[0].message.content.trim();
        
        // Atualizar o campo de descrição com o conteúdo extraído
        handleUpdate('description', generatedContent);
        
        toast({
          title: "Descrição processada",
          description: "A descrição foi melhorada com sucesso pela IA.",
          variant: "default"
        });
      }
    } catch (error) {
      console.error('Erro ao processar descrição:', error);
      toast({
        title: "Erro",
        description: "Houve um erro ao processar a descrição. Por favor, tente novamente.",
        variant: "destructive"
      });
    } finally {
      setIsProcessingDescription(false);
    }
  };

  // Format currency values
  const formatCurrency = (value: number | null | undefined): string => {
    if (value === null || value === undefined) return '';
    return new Intl.NumberFormat('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  // Parse currency input
  const parseCurrency = (value: string): number | null => {
    if (!value) return null;
    const numericValue = value.replace(/\D/g, '');
    return numericValue ? Number(numericValue) / 100 : null;
  };

  // Function to calculate arrival date and time based on departure date, time and duration
  const calculateArrival = (departureDate, departureTime, duration) => {
    if (!departureDate || !departureTime || !duration || isCalculatingTimes) return;
    
    setIsCalculatingTimes(true);
    
    try {
      // Parse the duration in format HH:MM
      const [hours, minutes] = duration.split(':').map(num => parseInt(num, 10));
      
      // Create a JavaScript Date object for the departure time
      const departure = new Date(`${departureDate}T${departureTime}`);
      
      // Add duration to the departure time
      const arrival = new Date(departure);
      arrival.setHours(arrival.getHours() + hours);
      arrival.setMinutes(arrival.getMinutes() + minutes);
      
      // Format the arrival date and time
      const arrivalDate = arrival.toISOString().split('T')[0];
      const arrivalTime = arrival.toTimeString().substring(0, 5);
      
      // Update the transportation object
      if (batchUpdateTransportation) {
        batchUpdateTransportation(transportation.id, {
          arrivalDate,
          arrivalTime
        });
      } else {
        updateTransportation(pageId, transportation.id, 'arrivalDate', arrivalDate);
        updateTransportation(pageId, transportation.id, 'arrivalTime', arrivalTime);
      }
    } catch (error) {
      console.error("Error calculating arrival time:", error);
    } finally {
      setIsCalculatingTimes(false);
    }
  };
  
  // Function to calculate duration based on departure and arrival date/times
  const calculateDuration = (departureDate, departureTime, arrivalDate, arrivalTime) => {
    if (!departureDate || !departureTime || !arrivalDate || !arrivalTime || isCalculatingTimes) return;
    
    setIsCalculatingTimes(true);
    
    try {
      // Create JavaScript Date objects
      const departure = new Date(`${departureDate}T${departureTime}`);
      const arrival = new Date(`${arrivalDate}T${arrivalTime}`);
      
      // Check if arrival is before departure
      if (arrival < departure) {
        toast({
          title: "Erro na duração",
          description: "A data/hora de chegada deve ser após a data/hora de partida.",
          variant: "destructive"
        });
        return;
      }
      
      // Calculate difference in milliseconds
      const diff = arrival.getTime() - departure.getTime();
      
      // Convert to hours and minutes
      const totalMinutes = Math.floor(diff / 60000);
      const hours = Math.floor(totalMinutes / 60);
      const minutes = totalMinutes % 60;
      
      // Format the duration
      const duration = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
      
      // Update the transportation object
      updateTransportation(pageId, transportation.id, 'duration', duration);
    } catch (error) {
      console.error("Error calculating duration:", error);
    } finally {
      setIsCalculatingTimes(false);
    }
  };

  // Watch for changes in departure date/time and duration to calculate arrival
  useEffect(() => {
    if (transportation.date && transportation.time && transportation.duration) {
      calculateArrival(transportation.date, transportation.time, transportation.duration);
    }
  }, [transportation.date, transportation.time, transportation.duration]);

  // Watch for changes in departure and arrival date/times to calculate duration
  useEffect(() => {
    if (transportation.date && transportation.time && 
        transportation.arrivalDate && transportation.arrivalTime) {
      calculateDuration(
        transportation.date, 
        transportation.time, 
        transportation.arrivalDate, 
        transportation.arrivalTime
      );
    }
  }, [transportation.date, transportation.time, transportation.arrivalDate, transportation.arrivalTime]);

  // Custom handler for time-related fields to prevent circular updates
  const handleTimeUpdate = (field, value) => {
    // Clear the calculated fields when manually editing
    if (field === 'date' || field === 'time') {
      if (transportation.arrivalDate && transportation.arrivalTime && transportation.duration) {
        // Keep duration, recalculate arrival
        handleUpdate(field, value);
      } else {
        // Just update the field
        handleUpdate(field, value);
      }
    } 
    else if (field === 'arrivalDate' || field === 'arrivalTime') {
      // When manually editing arrival, clear duration to avoid conflicts
      handleUpdate(field, value);
    }
    else if (field === 'duration') {
      // When manually editing duration, clear arrival to avoid conflicts
      handleUpdate(field, value);
    }
    else {
      // For other fields, use the normal update handler
      handleUpdate(field, value);
    }
  };

  // Handler para o botão de salvar na biblioteca
  const handleSaveToLibrary = (e: React.MouseEvent) => {
    e.stopPropagation(); // Impedir que o clique colapse/expanda o item
    setIsSaveToLibraryOpen(true);
  };

  // Finalizar edição do título
  const handleFinishTitleEdit = () => {
    setIsEditingTitle(false);
    if (transportationTitle.trim() !== '') {
      handleUpdate('name', transportationTitle.trim());
    } else {
      handleUpdate('name', '');
      setTransportationTitle(`Traslado #${index + 1}`);
    }
  };

  return (
    <>
      <Card className={cn(
        "border shadow-sm transition-all border-l-4 border-l-amber-500",
        isCollapsed && "border-transparent border-l-amber-500"
      )}>
        <CardHeader 
          className={cn(
            "cursor-pointer flex items-center justify-between",
            isCollapsed ? "py-2 px-3" : "py-3 px-4"
          )}
          onClick={onToggleCollapse}
        >
          <div className="flex items-center gap-2 w-full">
            <Car className="h-4 w-4 text-amber-500" />
            
            {isEditingTitle ? (
              <div className="flex items-center" onClick={(e) => e.stopPropagation()}>
                <Input
                  value={transportationTitle}
                  onChange={(e) => setTransportationTitle(e.target.value)}
                  className="h-7 mr-2 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:border-transparent"
                  autoFocus
                  onFocus={() => {
                    // Se for apenas o placeholder, limpe o campo para edição
                    if (!transportation.name || transportation.name.trim() === '') {
                      setTransportationTitle('');
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleFinishTitleEdit();
                    }
                  }}
                  onBlur={handleFinishTitleEdit}
                />
              </div>
            ) : (
              <>
                {isCollapsed ? (
                  <span className="font-medium">{transportation.name || `Traslado #${index + 1}`}</span>
                ) : (
                  <div 
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsEditingTitle(true);
                      if (!transportation.name || transportation.name.trim() === '') {
                        setTransportationTitle('');
                      }
                    }}
                  >
                    <h3 className="font-medium">{transportation.name || `Traslado #${index + 1}`}</h3>
                  </div>
                )}
              </>
            )}
            
            {/* Quick info for collapsed view */}
            {isCollapsed && (
              <>
                {transportation.departureLocation && transportation.arrivalLocation && (
                  <div className="flex items-center ml-3 text-sm text-gray-500">
                    <ArrowLeftRight className="h-3.5 w-3.5 mr-1" />
                    <span>
                      {transportation.departureLocation} → {transportation.arrivalLocation}
                    </span>
                  </div>
                )}
                
                {transportation.date && (
                  <div className="flex items-center ml-3 text-sm text-gray-500">
                    <Calendar className="h-3.5 w-3.5 mr-1" />
                    <span>{transportation.date}</span>
                  </div>
                )}
                
                {transportation.time && (
                  <div className="flex items-center ml-3 text-sm text-gray-500">
                    <Clock className="h-3.5 w-3.5 mr-1" />
                    <span>{transportation.time}</span>
                  </div>
                )}

                {transportation.price !== undefined && transportation.price !== null && (
                  <div className="flex items-center ml-3 text-sm text-gray-500">
                    <DollarSign className="h-3.5 w-3.5 mr-1" />
                    <span>{formatCurrency(transportation.price)} {transportation.currency || 'BRL'}</span>
                  </div>
                )}

                {transportation.importantInfo && (
                  <div className="flex items-center ml-3 mt-1 text-sm text-white bg-orange-500/90 px-2 py-0.5 rounded">
                    <Info className="h-3.5 w-3.5 mr-1" />
                    <span>{transportation.importantInfo}</span>
                  </div>
                )}
              </>
            )}
            
            <div className="flex items-center ml-auto">
              {/* Botão de Salvar na Biblioteca (bookmark) */}
              <Button
                variant={isSavedToLibrary ? "default" : "ghost"}
                size="icon"
                className={cn(
                  "h-8 w-8 transition-colors",
                  isSavedToLibrary 
                    ? "bg-amber-500 hover:bg-amber-600" 
                    : "hover:text-amber-500"
                )}
                onClick={(e) => {
                  e.stopPropagation();
                  setIsSaveToLibraryOpen(true);
                }}
                title={isSavedToLibrary ? "Item salvo na biblioteca" : "Salvar na Biblioteca"}
              >
                <Bookmark className={cn(
                  "h-4 w-4", 
                  isSavedToLibrary 
                    ? "text-white" 
                    : "text-gray-500 hover:text-amber-500"
                )} />
              </Button>
              
              {/* Dropdown Menu de Ações */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                  >
                    <MoreHorizontal className="h-4 w-4 text-gray-500" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-[180px]">
                  {duplicateTransportation && (
                    <DropdownMenuItem 
                      onClick={(e) => {
                        e.stopPropagation();
                        duplicateTransportation(pageId, transportation.id);
                      }}
                    >
                      <Copy className="h-4 w-4 mr-2" />
                      <span>Duplicar</span>
                    </DropdownMenuItem>
                  )}
                  
                  {moveTransportationToPage && (
                    <DropdownMenuItem 
                      onClick={(e) => {
                        e.stopPropagation();
                        moveTransportationToPage(transportation.id);
                      }}
                    >
                      <ArrowLeftRight className="h-4 w-4 mr-2" />
                      <span>Mover para outra página</span>
                    </DropdownMenuItem>
                  )}
                  
                  <DropdownMenuItem
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteTransportation(pageId, transportation.id);
                    }}
                    className="text-red-600"
                  >
                    <Trash className="h-4 w-4 mr-2" />
                    <span>Excluir</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              
              {isCollapsed ? (
                <ChevronRight className="h-5 w-5 text-gray-500" />
              ) : (
                <ChevronDown className="h-5 w-5 text-gray-500" />
              )}
            </div>
          </div>
        </CardHeader>

        {!isCollapsed && (
          <CardContent className="p-4 pt-0 space-y-4">
            {/* Name, Departure Location and Arrival Location fields side by side */}
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2 flex flex-col">
                <div className="flex flex-col items-start">
                  <StyledLabel
                    htmlFor={`name-${transportation.id}`}
                    icon={Tag}
                  >
                    Nome do Traslado
                  </StyledLabel>
                  <div className="relative w-full">
                    <FormInput
                      id={`name-${transportation.id}`}
                      value={transportationTitle}
                      onChange={(e) => setTransportationTitle(e.target.value)}
                      onFocus={() => {
                        // Se for apenas o placeholder, limpe o campo para edição
                        if (!transportation.name || transportation.name.trim() === '') {
                          setTransportationTitle('');
                        }
                      }}
                      onBlur={handleFinishTitleEdit}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          handleFinishTitleEdit();
                        }
                      }}
                      placeholder="Nome do traslado ou serviço"
                    />
                  </div>
                </div>
              </div>
              
              {/* Departure Location with Google Places integration */}
              <div className="space-y-2 flex flex-col">
                <div className="flex flex-col items-start">
                  <StyledLabel
                    htmlFor={`departure-location-${transportation.id}`}
                    icon={MapPin}
                  >
                    Local de Partida
                  </StyledLabel>
                </div>
                <PlacesAutocompleteV1
                  value={transportation.departureLocation || ''}
                  onChange={(value) => handleUpdate('departureLocation', value)}
                  onPlaceSelect={(place) => {
                    if (place) {
                      console.log("[TransportationForm] Departure Place selected:", place);
                      
                      // Create an updates object with all location-related fields
                      const updates: Partial<TransportationInfo> = {
                        departurePlaceId: place.placeId,
                        departureGoogleMapsUrl: place.googleMapsUrl,
                        departureAddress: place.address,
                        departureLocation: place.name,
                        departureCoordinates: place.coordinates ? {
                          lat: Number(place.coordinates.lat),
                          lng: Number(place.coordinates.lng)
                        } : undefined,
                        // Adicionar campos de cidade e país para o local de partida
                        departureCity: place.city,
                        departureCountry: place.country
                      };
                      
                      // Use the batch update function if available, otherwise update fields individually
                      if (batchUpdateTransportation) {
                        batchUpdateTransportation(transportation.id, updates);
                      } else {
                        // Fall back to individual updates
                        Object.entries(updates).forEach(([field, value]) => {
                          const key = field as keyof TransportationInfo;
                          updateTransportation(pageId, transportation.id, key, value);
                        });
                      }
                      
                      // Notify user about the location confirmation
                      toast({
                        title: "Local de partida confirmado",
                        description: `${place.name} foi vinculado como ponto de partida.`,
                      });
                    }
                  }}
                  placeholder="Digite o local de partida"
                />
              </div>

              {/* Arrival Location with Google Places integration */}
              <div className="space-y-2 flex flex-col">
                <div className="flex flex-col items-start">
                  <StyledLabel
                    htmlFor={`arrival-location-${transportation.id}`}
                    icon={MapPin}
                  >
                    Local de Chegada
                  </StyledLabel>
                </div>
                <PlacesAutocompleteV1
                  value={transportation.arrivalLocation || ''}
                  onChange={(value) => handleUpdate('arrivalLocation', value)}
                  onPlaceSelect={(place) => {
                    if (place) {
                      console.log("[TransportationForm] Arrival Place selected:", place);
                      
                      // Create an updates object with all location-related fields
                      const updates: Partial<TransportationInfo> = {
                        arrivalPlaceId: place.placeId,
                        arrivalGoogleMapsUrl: place.googleMapsUrl,
                        arrivalAddress: place.address,
                        arrivalLocation: place.name,
                        arrivalCoordinates: place.coordinates ? {
                          lat: Number(place.coordinates.lat),
                          lng: Number(place.coordinates.lng)
                        } : undefined,
                        // Adicionar campos de cidade e país para o local de chegada
                        arrivalCity: place.city,
                        arrivalCountry: place.country
                      };
                      
                      // Use the batch update function if available, otherwise update fields individually
                      if (batchUpdateTransportation) {
                        batchUpdateTransportation(transportation.id, updates);
                      } else {
                        // Fall back to individual updates
                        Object.entries(updates).forEach(([field, value]) => {
                          const key = field as keyof TransportationInfo;
                          updateTransportation(pageId, transportation.id, key, value);
                        });
                      }
                      
                      // Notify user about the location confirmation
                      toast({
                        title: "Local de chegada confirmado",
                        description: `${place.name} foi vinculado como ponto de chegada.`,
                      });
                    }
                  }}
                  placeholder="Digite o local de chegada"
                />
              </div>
            </div>
            
            {/* Description with rich text editor */}
            <div className="space-y-2 flex flex-col">
              <div className="flex items-center justify-between">
                <div className="flex flex-col items-start">
                  <StyledLabel
                    htmlFor={`description-${transportation.id}`}
                    icon={AlignLeft}
                  >
                    Descrição
                  </StyledLabel>
                </div>
              </div>
              <RichTextEditor
                value={transportation.description || ''}
                onChange={(value) => handleUpdate('description', value)}
                placeholder="Descreva o traslado..."
                onProcessWithAI={handleProcessDescription}
                isProcessing={isProcessingDescription}
              />
            </div>
            
            {/* Date and Time fields */}
            <div className="grid grid-cols-5 gap-4">
              <div className="space-y-2 flex flex-col">
                <div className="flex flex-col items-start">
                  <StyledLabel
                    htmlFor={`date-${transportation.id}`}
                    icon={Calendar}
                  >
                    Data de Partida
                  </StyledLabel>
                </div>
                <FormInput
                  type="date"
                  id={`date-${transportation.id}`}
                  value={transportation.date || ''}
                  onChange={(e) => handleTimeUpdate('date', e.target.value)}
                />
              </div>
              
              <div className="space-y-2 flex flex-col">
                <div className="flex flex-col items-start">
                  <StyledLabel
                    htmlFor={`time-${transportation.id}`}
                    icon={Clock}
                  >
                    Horário de Partida
                  </StyledLabel>
                </div>
                <FormInput
                  type="time"
                  id={`time-${transportation.id}`}
                  value={transportation.time || ''}
                  onChange={(e) => handleTimeUpdate('time', e.target.value)}
                />
              </div>
              
              <div className="space-y-2 flex flex-col">
                <div className="flex flex-col items-start">
                  <StyledLabel
                    htmlFor={`duration-${transportation.id}`}
                    icon={Clock}
                  >
                    Duração
                  </StyledLabel>
                </div>
                <FormInput
                  type="time"
                  id={`duration-${transportation.id}`}
                  value={transportation.duration || ''}
                  onChange={(e) => handleTimeUpdate('duration', e.target.value)}
                  placeholder="00:00"
                />
              </div>

              <div className="space-y-2 flex flex-col">
                <div className="flex flex-col items-start">
                  <StyledLabel
                    htmlFor={`arrival-date-${transportation.id}`}
                    icon={Calendar}
                  >
                    Data de Chegada
                  </StyledLabel>
                </div>
                <FormInput
                  type="date"
                  id={`arrival-date-${transportation.id}`}
                  value={transportation.arrivalDate || ''}
                  onChange={(e) => handleTimeUpdate('arrivalDate', e.target.value)}
                />
              </div>
              
              <div className="space-y-2 flex flex-col">
                <div className="flex flex-col items-start">
                  <StyledLabel
                    htmlFor={`arrival-time-${transportation.id}`}
                    icon={Clock}
                  >
                    Horário de Chegada
                  </StyledLabel>
                </div>
                <FormInput
                  type="time"
                  id={`arrival-time-${transportation.id}`}
                  value={transportation.arrivalTime || ''}
                  onChange={(e) => handleTimeUpdate('arrivalTime', e.target.value)}
                />
              </div>
            </div>
            
            {/* Important Info */}
            <div className="space-y-2 flex flex-col">
              <div className="flex flex-col items-start">
                <StyledLabel
                  htmlFor={`important-info-${transportation.id}`}
                  icon={Info}
                >
                  Informações Importantes
                </StyledLabel>
              </div>
              <FormTextarea
                id={`important-info-${transportation.id}`}
                value={transportation.importantInfo || ''}
                onChange={(e) => handleUpdate('importantInfo', e.target.value)}
                placeholder="Informações importantes que o cliente deve saber"
                rows={3}
              />
            </div>
            
            {/* Price section */}
            <div className="space-y-3 pt-2 border-t">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2 flex flex-col">
                  <div className="flex flex-col items-start">
                    <StyledLabel
                      htmlFor={`price-${transportation.id}`}
                      icon={DollarSign}
                    >
                      Preço
                    </StyledLabel>
                  </div>
                  <div className="relative">
                    <DollarSign className="h-4 w-4 absolute left-2.5 top-2.5 text-gray-500" />
                    <FormInput
                      id={`price-${transportation.id}`}
                      value={transportation.price ? formatCurrency(transportation.price) : ''}
                      onChange={(e) => {
                        const parsed = parseCurrency(e.target.value);
                        handleUpdate('price', parsed);
                      }}
                      className="pl-8"
                      placeholder="0,00"
                    />
                  </div>
                </div>
                <div className="space-y-2 flex flex-col">
                  <div className="flex flex-col items-start">
                    <StyledLabel
                      htmlFor={`currency-${transportation.id}`}
                      icon={Tag}
                    >
                      Moeda
                    </StyledLabel>
                  </div>
                  <FormInput
                    id={`currency-${transportation.id}`}
                    value={transportation.currency || 'BRL'}
                    onChange={(e) => handleUpdate('currency', e.target.value)}
                    placeholder="BRL"
                  />
                </div>
              </div>
            </div>
            
            {/* Image management */}
            <div className="pt-4 border-t space-y-2">
              <div className="flex justify-between items-center">
                <div className="flex flex-col items-start">
                  <StyledLabel
                    className="text-sm font-medium"
                    icon={Image}
                  >
                    Imagens
                  </StyledLabel>
                </div>
                <div className="flex items-center gap-2">
                  {transportation.images && transportation.images.length > 0 ? (
                    <div className="flex items-center gap-1">
                      <div 
                        className="h-8 w-8 rounded-md overflow-hidden border border-gray-200 shadow-sm hover:border-blue-500 cursor-pointer transition-all hover:scale-105"
                        onClick={(e) => {
                          e.stopPropagation();
                          setIsImageManagerOpen(true);
                        }}
                      >
                        <img 
                          src={transportation.images[0]} 
                          alt="Miniatura" 
                          className="h-full w-full object-cover"
                        />
                      </div>
                      {transportation.images.length > 1 && (
                        <span 
                          className="text-xs text-gray-500 cursor-pointer hover:text-blue-500"
                          onClick={(e) => {
                            e.stopPropagation();
                            setIsImageManagerOpen(true);
                          }}
                        >
                          +{transportation.images.length - 1}
                        </span>
                      )}
                    </div>
                  ) : null}
                  <Button 
                    type="button" 
                    variant="outline" 
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsImageManagerOpen(true);
                    }}
                    className="h-8 bg-white border-gray-200 hover:bg-gray-50 text-blue-500"
                  >
                    <ImagePlus className="h-4 w-4 mr-2" />
                    <span>Gerenciar imagens</span>
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Modal para Salvar na Biblioteca */}
      {isSaveToLibraryOpen && (
        <SaveToLibraryModal
          isOpen={isSaveToLibraryOpen}
          onClose={() => setIsSaveToLibraryOpen(false)}
          onSave={(savedItem) => {
            setIsSavedToLibrary(true);
            toast({
              title: "Item salvo na biblioteca",
              description: "O traslado foi salvo com sucesso na sua biblioteca.",
            });
            setIsSaveToLibraryOpen(false);
          }}
          item={transportation}
          itemType="transportation"
        />
      )}
      
      {isImageManagerOpen && (
        <ImageManagerModal
          isOpen={isImageManagerOpen}
          onClose={() => setIsImageManagerOpen(false)}
          images={transportation.images || []}
          onImagesChange={(images) => {
            handleUpdate('images', images);
          }}
          contextId={transportation.id}
          contextType="transportation"
        />
      )}
    </>
  );
} 